import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Facebook,
  Instagram,
  MessageCircle,
  Sparkles,
  Star,
  Headphones,
  Globe,
  CheckCircle
} from "lucide-react";
import strLogo from "@/assets/str-logo.png";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useState } from "react";

// TikTok icon component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Contact = () => {
  const { getConfig } = useSiteConfig();
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Number",
      details: getConfig('contact_phone', '+1 (809) 123-4567'),
      description: "Available 24/7 for bookings and emergencies",
      color: "from-primary to-blue-600",
      action: `tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`
    },
    {
      icon: Mail,
      title: "Email Address",
      details: getConfig('contact_email', 'info@strtransfers.com'),
      description: "We respond within 2 hours during business hours",
      color: "from-secondary to-red-500",
      action: `mailto:${getConfig('contact_email', 'info@strtransfers.com')}`
    },
    {
      icon: MapPin,
      title: "Our Location",
      details: getConfig('contact_location', 'Punta Cana, Dominican Republic'),
      description: "Serving all major destinations in DR",
      color: "from-tropical to-yellow-500",
      action: null
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "24/7 Service Available",
      description: "Airport transfers and emergencies anytime",
      color: "from-palm to-green-500",
      action: null
    }
  ];

  const serviceAreas = getConfig('contact_service_areas', [
    "Punta Cana",
    "Bavaro",
    "Cap Cana",
    "Uvero Alto",
    "La Romana",
    "Santo Domingo",
    "Samaná",
    "Puerto Plata"
  ]);

  const socialLinks = [
    { 
      icon: Facebook, 
      name: "Facebook", 
      url: getConfig('social_facebook')?.url || '#',
      enabled: getConfig('social_facebook')?.enabled || true,
      color: "hover:bg-blue-600"
    },
    { 
      icon: Instagram, 
      name: "Instagram", 
      url: getConfig('social_instagram')?.url || '#',
      enabled: getConfig('social_instagram')?.enabled || true,
      color: "hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500"
    },
    { 
      icon: TikTokIcon, 
      name: "TikTok", 
      url: getConfig('social_tiktok')?.url || '#',
      enabled: getConfig('social_tiktok')?.enabled || true,
      color: "hover:bg-black"
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    // Simulate form submission
    setTimeout(() => setFormSubmitting(false), 2000);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
        
        {/* Animated Mesh */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-palm/40 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-tropical/50 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-white/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-1/6 animate-float">
          <MessageCircle className="w-8 h-8 text-white/40" />
        </div>
        <div className="absolute bottom-1/3 right-1/5 animate-float animation-delay-2000">
          <Mail className="w-10 h-10 text-tropical/50" />
        </div>
        <div className="absolute top-1/3 right-1/4 animate-float animation-delay-4000">
          <Phone className="w-6 h-6 text-white/30" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            {/* Logo */}
            <div className="mb-8 flex justify-center animate-fade-in-up">
              <img 
                src={strLogo} 
                alt="Servicio Turístico Rachel" 
                className="h-20 w-auto drop-shadow-2xl"
              />
            </div>

            <Badge className="mb-6 bg-white/20 backdrop-blur-sm text-white border-white/30 text-lg py-2 px-6 animate-fade-in-up animation-delay-200">
              <Sparkles className="w-4 h-4 mr-2" />
              Get in Touch
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up animation-delay-400">
              <span className="block">Let's</span>
              <span className="bg-gradient-to-r from-tropical via-white to-palm bg-clip-text text-transparent">
                Connect
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto animate-fade-in-up animation-delay-600">
              Ready to explore the Dominican Republic? Our team is here 24/7 to help 
              plan your perfect Caribbean adventure.
            </p>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0 text-base py-2 px-6">
              <Headphones className="w-4 h-4 mr-2" />
              Contact Information
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">We're Here</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> to Help</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the most convenient way to reach us. We're committed to quick responses!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card 
                key={index} 
                className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-card animate-fade-in-up cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => info.action && window.open(info.action, '_self')}
              >
                {/* Gradient Top Border */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${info.color}`}></div>
                
                {/* Hover Glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${info.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                
                <CardHeader className="text-center pb-2">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${info.color} mx-auto mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <info.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-lg">{info.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="font-semibold text-primary mb-2 text-lg">{info.details}</div>
                  <CardDescription className="text-sm">{info.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-muted/30 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <Badge className="mb-6 bg-gradient-to-r from-tropical to-palm text-white border-0 text-base py-2 px-6">
                <Send className="w-4 h-4 mr-2" />
                Send a Message
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-foreground">Quick</span>
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Contact Form</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Fill out the form below and we'll get back to you within 2 hours during business hours
              </p>
            </div>

            <Card className="border-0 shadow-2xl overflow-hidden animate-fade-in-up animation-delay-200">
              {/* Gradient Header */}
              <div className="h-2 bg-gradient-to-r from-primary via-secondary to-tropical"></div>
              
              <CardHeader className="text-center pt-8">
                <CardTitle className="text-2xl md:text-3xl">Book or Inquire</CardTitle>
                <CardDescription className="text-base">
                  Whether you want to book a service or just have questions, we're here to help
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 group">
                      <Label htmlFor="firstName" className="text-base font-medium">First Name *</Label>
                      <Input 
                        id="firstName" 
                        placeholder="Your first name" 
                        required 
                        className="h-12 border-2 focus:border-primary transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-base font-medium">Last Name *</Label>
                      <Input 
                        id="lastName" 
                        placeholder="Your last name" 
                        required 
                        className="h-12 border-2 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-medium">Email Address *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="your.email@example.com" 
                        required 
                        className="h-12 border-2 focus:border-primary transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-base font-medium">Phone Number</Label>
                      <Input 
                        id="phone" 
                        placeholder="+1 (xxx) xxx-xxxx" 
                        className="h-12 border-2 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-base font-medium">Service Type *</Label>
                      <select 
                        id="service" 
                        className="w-full h-12 px-4 border-2 border-input rounded-md bg-background text-foreground focus:border-primary transition-colors"
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="airport-transfer">Airport Transfer</option>
                        <option value="private-tour">Private Tour</option>
                        <option value="group-transport">Group Transportation</option>
                        <option value="custom-package">Custom Package</option>
                        <option value="general-inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date" className="text-base font-medium">Preferred Date</Label>
                      <Input 
                        id="date" 
                        type="date" 
                        className="h-12 border-2 focus:border-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guests" className="text-base font-medium">Number of Guests</Label>
                    <select 
                      id="guests" 
                      className="w-full h-12 px-4 border-2 border-input rounded-md bg-background text-foreground focus:border-primary transition-colors"
                    >
                      <option value="">Select number of guests</option>
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3-4">3-4 Guests</option>
                      <option value="5-8">5-8 Guests</option>
                      <option value="9-15">9-15 Guests</option>
                      <option value="16+">16+ Guests</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-base font-medium">Message *</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Please provide details about your request, including pickup location, destination, special requirements, or any questions you have..."
                      rows={5}
                      required
                      className="border-2 focus:border-primary transition-colors resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full h-14 bg-gradient-to-r from-primary via-secondary to-tropical hover:opacity-90 text-white text-lg font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                  >
                    {formSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-blue-600 text-white border-0 text-base py-2 px-6">
              <Globe className="w-4 h-4 mr-2" />
              Service Coverage
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Where We</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Operate</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Premium transportation services throughout the Dominican Republic
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 max-w-5xl mx-auto">
            {serviceAreas.map((area, index) => (
              <Card 
                key={index} 
                className="group text-center p-4 border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="text-primary font-semibold group-hover:text-secondary transition-colors">{area}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact Section */}
      <section className="py-20 bg-muted/30 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <Card className="relative overflow-hidden border-0 shadow-2xl">
              {/* Animated Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-tropical"></div>
              
              {/* Floating Elements */}
              <div className="absolute top-10 right-10 animate-float opacity-30">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div className="absolute bottom-10 left-10 animate-float animation-delay-2000 opacity-30">
                <Sparkles className="w-10 h-10 text-tropical" />
              </div>

              <CardContent className="relative z-10 text-center py-16 px-8 text-white">
                {/* Pulsing Phone Icon */}
                <div className="relative mx-auto w-24 h-24 mb-8">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-ping opacity-50"></div>
                  <div className="absolute inset-2 bg-white/30 rounded-full animate-ping opacity-30 animation-delay-500"></div>
                  <div className="relative w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Phone className="w-12 h-12" />
                  </div>
                </div>

                <h3 className="text-3xl md:text-4xl font-bold mb-4">24/7 Emergency Line</h3>
                <p className="text-xl text-white/90 mb-6 max-w-lg mx-auto">
                  For urgent assistance, emergency transportation, or last-minute bookings
                </p>
                
                <div className="text-4xl md:text-5xl font-bold mb-8 tracking-wide">
                  {getConfig('contact_phone', '+1 (809) 123-4567')}
                </div>
                
                <p className="text-white/80 mb-8">
                  Available 24 hours a day, 7 days a week for emergency situations, 
                  airport transfers, and urgent transportation needs
                </p>
                
                <Button 
                  size="lg" 
                  className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                  onClick={() => window.open(`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`, '_self')}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in-up">
            <Badge className="mb-6 bg-gradient-to-r from-secondary to-tropical text-white border-0 text-base py-2 px-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Stay Connected
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-foreground">Follow</span>
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Our Journey</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join our community for travel tips, destination highlights, and exclusive offers
            </p>
          </div>

          <div className="flex justify-center gap-6 animate-fade-in-up animation-delay-200">
            {socialLinks.map((social, index) => (
              <a 
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 hover:-rotate-6 ${social.color}`}
              >
                <social.icon className="w-8 h-8 md:w-10 md:h-10" />
              </a>
            ))}
          </div>

          {/* Quick Action Badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-12 animate-fade-in-up animation-delay-400">
            {[
              { icon: CheckCircle, text: "Fast Response" },
              { icon: Star, text: "5-Star Service" },
              { icon: Globe, text: "International" }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full"
              >
                <item.icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
